import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FaPhone, FaEnvelope, FaWhatsapp, FaMapMarkerAlt, FaPaperPlane, FaCheckCircle } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { contactApi } from '../services/api';
import './Contact.css';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const propertyId = searchParams.get('propertyId');
  const propertyTitle = searchParams.get('propertyTitle');

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      subject: propertyTitle ? `Consulta sobre: ${propertyTitle}` : '',
      message: propertyTitle ? `Hola, me interesa la propiedad "${propertyTitle}". ¿Podría darme más información?` : '',
    }
  });

  const onSubmit = async (data) => {
    setSending(true);
    try {
      const payload = { ...data };
      if (propertyId) payload.propertyId = parseInt(propertyId);

      await contactApi.send(payload);
      setSent(true);
      reset();
      toast.success('¡Mensaje enviado! Te contactaremos pronto.');
    } catch (error) {
      const msg = error.response?.data?.message || 'Error al enviar. Intenta de nuevo.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-page">
      <Navbar />

      {/* Hero */}
      <div className="contact-hero">
        <div className="container">
          <div className="contact-hero__tag">¿Tienes preguntas?</div>
          <h1>Contáctanos</h1>
          <p>Estamos aquí para ayudarte a encontrar tu hogar ideal</p>
        </div>
      </div>

      <div className="container contact-body">
        {/* Info de contacto */}
        <div className="contact-info">
          <h2>Información de Contacto</h2>
          <p className="contact-info__subtitle">
            Nuestro equipo está disponible para atenderte de lunes a sábado
          </p>

          <div className="contact-cards">
            <a href="tel:+16474700762" className="contact-card">
              <div className="contact-card__icon"><FaPhone /></div>
              <div>
                <strong>Canadá / Internacional</strong>
                <span>+1 (647) 4700762</span>
              </div>
            </a>
            <a href="tel:+50379416355" className="contact-card">
              <div className="contact-card__icon"><FaPhone /></div>
              <div>
                <strong>El Salvador — Carol</strong>
                <span>+503 7941-6355</span>
              </div>
            </a>
            <a href="tel:+50377960598" className="contact-card">
              <div className="contact-card__icon"><FaPhone /></div>
              <div>
                <strong>El Salvador — Lilian</strong>
                <span>+503 7796-0598</span>
              </div>
            </a>
            <a href="mailto:linareslilian27@gmail.com?subject=Consulta%20Casa%20Bonita" target="_blank" rel="noopener noreferrer" className="contact-card">
              <div className="contact-card__icon"><FaEnvelope /></div>
              <div>
                <strong>Email — Lilian</strong>
                <span>linareslilian27@gmail.com</span>
              </div>
            </a>
            <a href="mailto:clinares79@gmail.com?subject=Consulta%20Casa%20Bonita" target="_blank" rel="noopener noreferrer" className="contact-card">
              <div className="contact-card__icon"><FaEnvelope /></div>
              <div>
                <strong>Email — Carol</strong>
                <span>clinares79@gmail.com</span>
              </div>
            </a>
          </div>

          {/* WhatsApp */}
          <div className="whatsapp-cta">
            <FaWhatsapp />
            <div>
              <strong>¿Prefieres WhatsApp?</strong>
              <p>Escríbenos directamente y te respondemos al instante</p>
            </div>
            <a
              href="https://wa.me/16474700762?text=Hola!%20Me%20interesa%20saber%20más%20sobre%20sus%20propiedades."
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--whatsapp"
            >
              Abrir WhatsApp
            </a>
          </div>

          {/* Ubicación */}
          <div className="office-map">
            <h3><FaMapMarkerAlt /> Nuestra ubicación</h3>
            <p>El Salvador — Atendemos todo el territorio nacional</p>
          </div>
        </div>

        {/* Formulario */}
        <div className="contact-form-wrapper">
          <div className="contact-form-card">
            <h2>Envíanos un mensaje</h2>
            <p>Responderemos a la brevedad posible</p>

            {propertyTitle && (
              <div className="property-ref">
                Consultando sobre: <strong>{propertyTitle}</strong>
              </div>
            )}

            {sent ? (
              <div className="form-success">
                <div className="form-success__icon"><FaCheckCircle /></div>
                <h3>¡Mensaje enviado!</h3>
                <p>
                  Hemos recibido tu consulta y te contactaremos pronto.<br />
                  También recibirás una confirmación en tu correo.
                </p>
                <button
                  className="btn btn--navy"
                  onClick={() => setSent(false)}
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nombre completo *</label>
                    <input
                      {...register('name', {
                        required: 'El nombre es requerido',
                        minLength: { value: 2, message: 'Mínimo 2 caracteres' }
                      })}
                      placeholder="Tu nombre"
                      className={errors.name ? 'error' : ''}
                    />
                    {errors.name && <span className="form-error">{errors.name.message}</span>}
                  </div>

                  <div className="form-group">
                    <label>Correo electrónico *</label>
                    <input
                      type="email"
                      {...register('email', {
                        required: 'El email es requerido',
                        pattern: { value: /^\S+@\S+\.\S+$/, message: 'Email inválido' }
                      })}
                      placeholder="tu@email.com"
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <span className="form-error">{errors.email.message}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Teléfono (opcional)</label>
                    <input
                      {...register('phone')}
                      placeholder="+503 ..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Asunto *</label>
                    <input
                      {...register('subject', {
                        required: 'El asunto es requerido',
                        minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                      })}
                      placeholder="¿En qué podemos ayudarte?"
                      className={errors.subject ? 'error' : ''}
                    />
                    {errors.subject && <span className="form-error">{errors.subject.message}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label>Mensaje *</label>
                  <textarea
                    {...register('message', {
                      required: 'El mensaje es requerido',
                      minLength: { value: 10, message: 'Mínimo 10 caracteres' }
                    })}
                    placeholder="Cuéntanos más sobre lo que buscas..."
                    rows={5}
                    className={errors.message ? 'error' : ''}
                  />
                  {errors.message && <span className="form-error">{errors.message.message}</span>}
                </div>

                <button
                  type="submit"
                  className="btn btn--gold btn--full btn--lg"
                  disabled={sending}
                >
                  {sending ? (
                    <><span className="btn-spinner" /> Enviando...</>
                  ) : (
                    <><FaPaperPlane /> Enviar mensaje</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
